import { CATEGORIES } from '@/config/categories';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { countByCategory } from '@/utils/search';
import { CountBadge } from '@/components/ui/badge';
import { cn } from '@/utils/cn';
import type { Category } from '@/types';

// ============================================
// CATEGORY TABS
// ============================================

export function CategoryTabs() {
  const { category, setCategory } = useApp();
  const { allItems } = useData();

  // Calculate counts for each category
  const counts = countByCategory(allItems);

  return (
    <div className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex gap-1 overflow-x-auto py-2 scrollbar-hide" aria-label="Categories">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const count = counts[cat.id];
            const isActive = category === cat.id;

            return (
              <CategoryTab
                key={cat.id}
                id={cat.id}
                label={cat.label}
                icon={<Icon className="h-4 w-4" />}
                count={count}
                isActive={isActive}
                onClick={() => setCategory(cat.id)}
              />
            );
          })}
        </nav>
      </div>
    </div>
  );
}

// ============================================
// CATEGORY TAB
// ============================================

interface CategoryTabProps {
  id: Category;
  label: string;
  icon: React.ReactNode;
  count: number;
  isActive: boolean;
  onClick: () => void;
}

function CategoryTab({ id, label, icon, count, isActive, onClick }: CategoryTabProps) {
  // Show count for specific categories
  const showCount = ['inbox', 'favorites'].includes(id) || count > 0;
  const displayCount = id === 'all' ? undefined : count;

  // Tooltip for inbox explaining its purpose
  const tooltip = id === 'inbox' ? 'New items land here for processing' : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap',
        'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        isActive
          ? 'bg-brand-100 text-brand-700 shadow-sm'
          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      {icon}
      <span>{label}</span>
      {showCount && displayCount !== undefined && <CountBadge count={displayCount} />}
    </button>
  );
}

// ============================================
// MOBILE CATEGORY SELECTOR (for small screens)
// ============================================

export function CategorySelector() {
  const { category, setCategory } = useApp();
  const { allItems } = useData();
  const counts = countByCategory(allItems);

  return (
    <div className="sm:hidden bg-[var(--bg-secondary)] border-b border-[var(--border-primary)] px-4 py-3">
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as Category)}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 rounded-lg',
          'border border-[var(--border-primary)] bg-[var(--bg-primary)] text-sm font-medium text-[var(--text-secondary)]',
          'focus:outline-none focus:ring-2 focus:ring-brand-500'
        )}
        aria-label="Select category"
      >
        {CATEGORIES.map((cat) => {
          const count = counts[cat.id];
          const countLabel = cat.id !== 'all' ? ` (${count})` : '';
          return (
            <option key={cat.id} value={cat.id}>
              {cat.label}
              {countLabel}
            </option>
          );
        })}
      </select>
    </div>
  );
}
